import { useEffect, useState, Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { Popconfirm, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHome, faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import SidePanel from '../../../components/common/side-panel/SidePanel'
import MainPanel from '../../../components/common/main-panel/MainPanel'
import instance from '../../../utils/Axios'
import { toast } from 'react-toastify'
import { triggerGoogleAnalyticsEvent } from '../../../utils/Helper'
import {
  currentUserSubscription,
  getAllSubscriptionProducts,
  userSubscriptionUsage,
} from '../../../redux/actions/SubscriptionActions'
import TikBoxModal from '../../../components/common/modal/Modal'
import { disableClient, getClients } from '../../../redux/actions/ClientActions'
import { AssetDeleteIcon } from '../../../components/common/icons/DeleteIcon'
import { DownloadIcon } from '../../../components/common/icons/DownloadIcon'

const itemsPerPage = 10

const InviteClientsContent = () => {
  const dispatch = useDispatch()
  const {
    auth: { user },
    clients,
  } = useSelector((state) => state)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [clientsData, setClientsData] = useState(clients.data)
  const [paginatedItems, setPaginatedItems] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [openAddClientModal, setOpenAddClientModal] = useState(false)
  const [clientUserId, setClientUserId] = useState()
  const [clientAgreementSummary, setClientAgreementSummary] = useState([])
  const [clientName, setClientName] = useState()
  const [loadingAgreementsSummary, setLoadingAgreementsSummary] =
    useState(false)

  // Invoke when user click to request another page.
  const handlePageClick = (value) => {
    setCurrentPage(value)
  }

  const toggleAddClientModal = (value) => {
    triggerGoogleAnalyticsEvent('toggle_add_client_modal', {
      value,
      userId: user?.id,
    })
    setOpenAddClientModal(value)
  }

  const sendClientInvite = (e) => {
    e.preventDefault()
    setLoading(true)
    triggerGoogleAnalyticsEvent('invite-client', {
      userId: user?.id,
    })

    instance
      .post('/users/invite-client', { email, name })
      .then(() => {
        setEmail('')
        setName('')
        toast.success('Invite sent successfully!')
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || 'Invite cannot be sent!')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const getClientAgreementsSummary = (clientId) => {
    setLoadingAgreementsSummary(true)
    triggerGoogleAnalyticsEvent('get-client-agreements-summary', {
      userId: user?.id,
    })

    instance
      .get(`/user-agreements/${clientId}/summary`)
      .then((response) => {
        setClientAgreementSummary(response?.data?.message || [])
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message ||
            'Unable to fetch client agreements summary!',
        )
      })
      .finally(() => {
        setLoadingAgreementsSummary(false)
      })
  }

  const disableAgreementForClient = (templateId, disabled) => {
    // Make request to disable agreement for client
    setLoadingAgreementsSummary(true)
    triggerGoogleAnalyticsEvent('disable_agreement_for_client', {
      userId: user?.id,
    })

    instance
      .put(`/agreement-templates/${templateId}/disable/clients`, {
        clients: [clientUserId],
        disabled,
      })
      .then((response) => {
        setLoadingAgreementsSummary(true)
        toast.success('Agreement template disabled for client successfully!')

        const oldTemplate = clientAgreementSummary.find(
          (t) => t.templateId === templateId,
        )
        const updatedTemplate = {
          ...oldTemplate,
          disabled,
        }
        const updatedTemplates = clientAgreementSummary.map((t) =>
          t.templateId === templateId ? updatedTemplate : t,
        )
        setClientAgreementSummary(updatedTemplates)
        setLoadingAgreementsSummary(false)
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message ||
            'Unable to disable agreement template for client!',
        )
      })
      .finally(() => {
        setLoadingAgreementsSummary(false)
      })
  }

  const handleDisableClient = async (id, disabled) => {
    await dispatch(disableClient(id, disabled))
    triggerGoogleAnalyticsEvent('disable_client', {
      clientId: id,
      userId: user?.id,
    })
  }

  const selectClientName = (client) => {
    setClientName(client.name)
    setClientUserId(client.id)
  }

  const resetClientSelection = () => {
    setClientName()
    setClientUserId()
    setClientAgreementSummary([])
  }

  useEffect(() => {
    if (clientUserId) {
      // Get clients agreement info
      getClientAgreementsSummary(clientUserId)
    }
  }, [clientUserId])
  useEffect(() => {
    setClientsData(clients.data)
  }, [clients.loading])

  useEffect(() => {
    const offset = (currentPage - 1) * itemsPerPage
    const paginatedClients = clientsData.slice(offset, offset + itemsPerPage)
    setPaginatedItems([...paginatedClients])
  }, [currentPage, clientsData])

  return (
    <MainPanel>
      <div className="clients-container">
        <div className="clients-container__header">
          <h2 className="title">
            {clientName ? (
              <span
                onClick={resetClientSelection}
                style={{ cursor: 'pointer' }}>
                Manage Client {'>'} {clientName}
              </span>
            ) : (
              'Client Organisations'
            )}
          </h2>
          <div className="add-new">
            <button onClick={() => toggleAddClientModal(true)}>
              + Add Client
            </button>
          </div>
        </div>

        {!clientName && (
          <div className="clients-table">
            <div className="clients-table__header">
              <div className="clients-table__row">
                <div className="tikbox-col">
                  <span>Name ({clientsData.length})</span>
                </div>
                <div className="tikbox-col">
                  <span>Email</span>
                </div>
                <div className="tikbox-col">
                  <span>Status</span>
                </div>
                <div className="tikbox-col">
                  <span>Last login</span>
                </div>
                <div className="tikbox-col">
                  <span>Action</span>
                </div>
              </div>
            </div>
            <div className="clients-table__body">
              {!paginatedItems.length ? (
                <div style={{ textAlign: 'center' }}>No clients</div>
              ) : (
                paginatedItems.map((client, index) => {
                  return (
                    <Fragment key={index}>
                      <div className="clients-table__row">
                        <div className="tikbox-col">
                          <div className="tikbox-col__content">
                            {/* <span className="title">Name</span> */}
                            <span
                              style={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                              }}
                              className="content"
                              onClick={() => selectClientName(client)}>
                              {client.name}
                            </span>
                          </div>
                        </div>
                        <div className="tikbox-col">
                          <div className="tikbox-col__content">
                            <span className="title">Email</span>
                            <span className="content">{client.email}</span>
                          </div>
                        </div>
                        <div className="tikbox-col">
                          <div className="tikbox-col__content">
                            <span className="title">Status</span>
                            <span className="content">
                              {client.disabledAt ? 'Disabled' : 'Active'}
                            </span>
                          </div>
                        </div>
                        <div className="tikbox-col">
                          <div className="tikbox-col__content">
                            <span className="title">Last Login</span>
                            <span className="content">
                              {moment(client.lastLoginAt).format('DD/MM/YYYY')}
                            </span>
                          </div>
                        </div>
                        <div className="tikbox-col actions">
                          <div className="tikbox-col__content">
                            <Tooltip title="Client Dashboard">
                              <button
                                className="button home"
                                onClick={() => selectClientName(client)}>
                                <FontAwesomeIcon
                                  color="#5a5757"
                                  icon={faHome}
                                />
                              </button>
                            </Tooltip>
                            <Tooltip title="Disable">
                              <Popconfirm
                                title={`${
                                  client.disabledAt ? 'Enable' : 'Disable'
                                } the client`}
                                description={`Are you sure to ${
                                  client.disabledAt ? 'enable' : 'disable'
                                } this client?`}
                                okText="Yes"
                                cancelText="No"
                                onConfirm={() =>
                                  handleDisableClient(
                                    client.id,
                                    !client.disabledAt,
                                  )
                                }>
                                <button
                                  className={`button ${
                                    client.disabledAt ? 'enable' : 'disable'
                                  }`}>
                                  {client.disabledAt ? (
                                    <FontAwesomeIcon
                                      color="#5a5757"
                                      icon={faCheckCircle}
                                    />
                                  ) : (
                                    <AssetDeleteIcon
                                      style={{ stroke: '#0daa85' }}
                                    />
                                  )}
                                </button>
                              </Popconfirm>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </Fragment>
                  )
                })
              )}
            </div>
          </div>
        )}

        {clientName && (
          <>
            <div className="clients-table">
              <div className="clients-table__header">
                <div className="clients-table__row">
                  <div className="tikbox-col">
                    <span>Agreement Name ({clientsData.length})</span>
                  </div>
                  <div className="tikbox-col">
                    <span>No Of Times Issued</span>
                  </div>
                  <div className="tikbox-col">
                    <span>Completed</span>
                  </div>
                  <div className="tikbox-col">
                    <span>Royalty</span>
                  </div>
                  <div className="tikbox-col">
                    <span>Status</span>
                  </div>
                  <div className="tikbox-col">
                    <span>Manage Agreement</span>
                  </div>
                </div>
              </div>
              <div className="clients-table__body">
                {!clientAgreementSummary.length ? (
                  <div style={{ textAlign: 'center' }}>No agreement</div>
                ) : (
                  clientAgreementSummary.map((client, index) => {
                    return (
                      <Fragment key={index}>
                        <div className="clients-table__row">
                          <div className="tikbox-col">
                            <div className="tikbox-col__content">
                              {/* <span className="title">Name</span> */}
                              <span className="content">
                                {client.agreementName}
                              </span>
                            </div>
                          </div>
                          <div className="tikbox-col">
                            <div className="tikbox-col__content">
                              <span className="title">No Of Times Issued</span>
                              <span className="content">
                                {client.noOfTimes}
                              </span>
                            </div>
                          </div>
                          <div className="tikbox-col">
                            <div className="tikbox-col__content">
                              <span className="title">Completed</span>
                              <span className="content">
                                {client.completed}
                              </span>
                            </div>
                          </div>
                          <div className="tikbox-col">
                            <div className="tikbox-col__content">
                              <span className="title">Royalty</span>
                              <span className="content">{client.royalty}</span>
                            </div>
                          </div>{' '}
                          <div className="tikbox-col">
                            <div className="tikbox-col__content">
                              <span className="title">Status</span>
                              <span className="content">
                                {client.disabled ? 'Disabled' : 'Active'}
                              </span>
                            </div>
                          </div>
                          <div className="tikbox-col actions">
                            <div className="tikbox-col__content">
                              {client.agreementUrl && (
                                <Tooltip title="Download Template PDF">
                                  <a
                                    className="button download"
                                    href={client.agreementUrl}
                                    target="_blank"
                                    rel="noreferrer">
                                    <DownloadIcon />
                                  </a>
                                </Tooltip>
                              )}
                              <Tooltip
                                title={
                                  client.disabled
                                    ? 'Enable the agreement'
                                    : 'Disable the agreement'
                                }>
                                <Popconfirm
                                  title={
                                    client.disabled
                                      ? 'Enable the agreement'
                                      : 'Disable the agreement'
                                  }
                                  description={`Are you sure to ${
                                    client.disabled ? 'enable' : 'disable'
                                  } this agreement for the client?`}
                                  okText="Yes"
                                  cancelText="No"
                                  onConfirm={() =>
                                    disableAgreementForClient(
                                      client.templateId,
                                      !client.disabled,
                                    )
                                  }>
                                  <button
                                    className={`button ${
                                      client.disabled ? 'enable' : 'disable'
                                    }`}>
                                    {client.disabled ? (
                                      <FontAwesomeIcon
                                        color="#5a5757"
                                        icon={faCheckCircle}
                                      />
                                    ) : (
                                      <AssetDeleteIcon
                                        style={{ stroke: '#0daa85' }}
                                      />
                                    )}
                                    {/* <AssetDeleteIcon
                                      style={{ stroke: '#0daa85' }}
                                    /> */}
                                  </button>
                                </Popconfirm>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </Fragment>
                    )
                  })
                )}
              </div>
            </div>
            {!loadingAgreementsSummary && (
              <p>
                Deleting an agreement removes the client's ability to use the
                agreement again. However, they will still be able to continue to
                use asserted agreements, and related assets.
              </p>
            )}
          </>
        )}
      </div>

      <TikBoxModal
        open={openAddClientModal}
        toggle={toggleAddClientModal}
        title="Invite New Client">
        <div className="template-agreement-form">
          <form onSubmit={sendClientInvite}>
            <div className="input-container">
              <p className="input-container__label">Client Name</p>
              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                }}
                type="text"
                placeholder="Client Name"
                required
              />
            </div>
            <div className="input-container">
              <p className="input-container__label">Client Email</p>
              <input
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                }}
                type="email"
                placeholder="Client Email"
                required
              />
            </div>

            <div className="action-btns">
              <button className="use-now-btn" type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </form>
        </div>
      </TikBoxModal>
    </MainPanel>
  )
}

const InviteClients = () => {
  const dispatch = useDispatch()

  const getSubscriptionUsage = async () => {
    await dispatch(userSubscriptionUsage())
  }

  const loadUserSubscription = async () => {
    await dispatch(currentUserSubscription())
  }

  const loadAllSubscriptionProducts = async () => {
    await dispatch(getAllSubscriptionProducts())
  }

  const handleGetClients = async () => {
    await dispatch(getClients())
  }

  useEffect(() => {
    getSubscriptionUsage()
    loadUserSubscription()
    loadAllSubscriptionProducts()
    handleGetClients()
  }, [])

  return (
    <div className="tikbox-invite-clients">
      <div className="tikbox-invite-clients__content">
        <SidePanel />
        <InviteClientsContent />
      </div>
    </div>
  )
}

export default InviteClients
